/**
 * Access → SaaS Catalog (SuperAdmin only, VSNP-1150)
 *
 * A GLOBAL catalog of popular SaaS apps and their FQDN match patterns, curated
 * by a SuperAdmin. Tenant admins consume it in the app-create modal (they pick
 * a profile instead of typing domains) but cannot edit it.
 *
 * A profile that any application references cannot be deleted — the API refuses
 * and this page surfaces that as a toast.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Pencil, Boxes } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SaasProfileModal from '../../components/access/SaasProfileModal';
import { useToast } from '../../components/common/ToastContainer';
import accessService, { type SaasProfile, type SaasProfileData } from '../../services/access.service';

/** Compact rendering of a profile's domains for the table. */
function domainsOf(profile: SaasProfile): string {
  const p = profile.fqdn_patterns ?? [];
  if (p.length === 0) return '—';
  return p.length <= 2 ? p.join(', ') : `${p.slice(0, 2).join(', ')} +${p.length - 2}`;
}

const SaasCatalog = () => {
  const toast = useToast();

  const [profiles, setProfiles] = useState<SaasProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<SaasProfile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SaasProfile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProfiles(await accessService.listSaasCatalog());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the SaaS catalog');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (profile: SaasProfile) => {
    setEditing(profile);
    setIsModalOpen(true);
  };

  const handleSave = async (data: SaasProfileData) => {
    try {
      if (editing) {
        await accessService.updateSaasProfile(editing.id, data);
        toast.success('Profile updated', data.name);
      } else {
        await accessService.createSaasProfile(data);
        toast.success('Profile created', data.name);
      }
      setIsModalOpen(false);
      setEditing(null);
      await fetchProfiles();
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the profile');
      throw err; // keeps the modal open
    }
  };

  // Enable/disable a profile in place. A disabled profile is hidden from the
  // tenant-admin app-create picker but keeps its links to existing apps.
  const handleToggle = async (profile: SaasProfile) => {
    setTogglingId(profile.id);
    try {
      await accessService.updateSaasProfile(profile.id, {
        slug: profile.slug,
        name: profile.name,
        fqdnPatterns: profile.fqdn_patterns,
        enabled: profile.enabled !== 1,
      });
      await fetchProfiles();
      toast.success(profile.enabled ? 'Profile disabled' : 'Profile enabled', profile.name);
    } catch (err) {
      toast.error('Could not update the profile', err instanceof Error ? err.message : 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteSaasProfile(deleteTarget.id);
      toast.success('Profile deleted', deleteTarget.name);
      setDeleteTarget(null);
      await fetchProfiles();
    } catch (err) {
      // The API refuses a delete while apps reference the profile — surface why.
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the profile');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<SaasProfile>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (p) => (
        <div>
          <div className="font-medium text-gray-900">{p.name}</div>
          <div className="text-xs text-gray-500">{p.slug}</div>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (p) =>
        p.category ? (
          <span className="text-sm text-gray-700">{p.category}</span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    { key: 'domains', label: 'Domains', render: domainsOf },
    {
      key: 'appCount',
      label: 'Apps',
      sortable: true,
      render: (p) =>
        p.appCount > 0 ? (
          <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            {p.appCount}
          </span>
        ) : (
          <span className="text-gray-400">0</span>
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (p) => (
        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={p.enabled === 1}
            busy={togglingId === p.id}
            onChange={() => handleToggle(p)}
            label={p.enabled ? `Disable ${p.name}` : `Enable ${p.name}`}
            title={p.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
          />
          <button
            type="button"
            className="text-gray-400 hover:text-primary-600"
            title="Edit profile"
            onClick={() => openEdit(p)}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40"
            title={p.appCount > 0 ? `In use by ${p.appCount} app(s) — cannot delete` : 'Delete profile'}
            disabled={p.appCount > 0}
            onClick={() => setDeleteTarget(p)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const enabledCount = profiles.filter((p) => p.enabled === 1).length;
  const inUseCount = profiles.filter((p) => p.appCount > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SaaS Catalog</h1>
          <p className="text-gray-600 mt-1">
            Global, curated SaaS application profiles that tenant admins pick from when defining SaaS access apps.
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchProfiles()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Add Profile
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <StatCard title="Profiles" value={profiles.length} icon={Boxes} color="primary" loading={loading} />
        <StatCard title="Enabled" value={enabledCount} icon={Boxes} color="success" loading={loading} />
        <StatCard title="In Use" value={inUseCount} icon={Boxes} color="info" loading={loading} />
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={profiles}
          loading={loading}
          searchPlaceholder="Search SaaS profiles…"
          emptyMessage="No SaaS profiles defined yet"
        />
      </div>

      <SaasProfileModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        profile={editing}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete SaaS profile"
        message={`Delete '${deleteTarget?.name}'? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default SaasCatalog;
